import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { api } from "helpers/axios";
import { errorMessage, scrollToTop } from "helpers/common";
import { detectCsvDelimiter } from "helpers/importFieldMatching";

import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileEncode from 'filepond-plugin-file-encode';

import 'filepond/dist/filepond.min.css';

import ErrorAlert from "components/ui/ErrorAlert";
import ImportPeriodWarningModal from "components/ui/ImportPeriodWarningModal";
import { isCSVFile } from "helpers/file";
import useImportPeriodGuard from "hooks/useImportPeriodGuard";
import { useTranslation } from "translation/useTranslation";
import { Button, Input, NativeSelect } from "components/ui";

registerPlugin(
  FilePondPluginFileValidateType,
  FilePondPluginFileEncode
);

export default function UserImportWizard({ setMode, handleImportClick }) {
  const { translate, editableTranslate, editableTranslateString } = useTranslation();

  const [files, setFiles] = useState([]);
  const [isCsv, setIsCsv] = useState(false);
  const [delimiter, setDelimiter] = useState(',');
  const [customDelimiter, setCustomDelimiter] = useState('');
  const [isAutoDetected, setIsAutoDetected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    checkingPeriods,
    handleCancelPeriodWarning,
    handleConfirmPeriodWarning,
    runWithPeriodGuard,
    warningState,
  } = useImportPeriodGuard(setError);

  useEffect(() => {
    if (files.length > 0 && isCSVFile(files[0])) {
      setIsCsv(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        const detected = detectCsvDelimiter(content);
        const knownDelimiters = [',', ';', '\t', '|'];
        if (knownDelimiters.includes(detected)) {
          setDelimiter(detected);
          setCustomDelimiter('');
        } else {
          setDelimiter('custom');
          setCustomDelimiter(detected);
        }
        setIsAutoDetected(true);
      };
      reader.readAsText(files[0].file.slice(0, 1024));
    } else {
      setIsCsv(false);
      setIsAutoDetected(false);
      setDelimiter(',');
      setCustomDelimiter('');
    }
  }, [files]);

  useEffect(() => {
    if (error) scrollToTop();
  }, [error]);

  const createImport = async (file) => {
    setLoading(true);

    try {
      const importFormData = new FormData();
      importFormData.append('file', file);
      importFormData.append('delimiter', delimiter === 'custom' ? customDelimiter : delimiter);

      const { data } = await api.post('/user-imports', importFormData, { headers: { 'Content-Type': 'multipart/form-data' } });

      handleImportClick(data);
    } catch (err) {
      setError(errorMessage(err, 'createImport'));
      setFiles([]);
    }

    setLoading(false);
  };

  const validateAndSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (files.length < 1) {
      setError(editableTranslate('user_import.no_file_uploaded', 'Keine Datei hochgeladen. Bitte laden Sie eine CSV- oder Excel-Datei hoch.'));
      return;
    }

    const fileItem = files[0];
    const file = fileItem.file;

    const validTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    if (!validTypes.includes(file.type)) {
      setError(editableTranslate('user_import.invalid_file_type', 'Ungültiger Dateityp. Bitte laden Sie eine CSV- oder Excel-Datei hoch.'));
      setFiles([]);
      return;
    }

    setLoading(true);

    try {
      // Step 1: validate file
      const validateFormData = new FormData();
      validateFormData.append('file', file);
      validateFormData.append('delimiter', delimiter === 'custom' ? customDelimiter : delimiter);

      await api.post('/user-imports/validate-file', validateFormData, { headers: { 'Content-Type': 'multipart/form-data' } });
    } catch (err) {
      setError(errorMessage(err, 'validateAndSubmit'));
      setFiles([]);
      setLoading(false);
      return;
    }

    setLoading(false);

    await runWithPeriodGuard(() => createImport(file));
  };

  return (
    <div className="mt-4">
      {error && (
        <div className="mb-3">
          <ErrorAlert error={error} onClose={() => setError(null)} />
        </div>
      )}

      <h4 className="mb-1">{editableTranslate('user_import.import_wizard.user_new_import_title', 'Neuer Import')}</h4>
      <div className="mb-3">{editableTranslate('user_import.import_wizard.user_new_import_description', 'Wählen Sie aus, wie Sie die Nutzendendaten einlesen möchten. Sie können eine Datei hochladen oder eine von Ihrem IT-Team vorkonfigurierte Quelle auswählen.')}</div>

      <div>
        <form>
          <div className={`flex-fill import-file-input ${files?.length > 0 ? 'has-file' : ''}`}>
            <FilePond
              name="file"
              files={files}
              acceptedFileTypes={['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']}
              onupdatefiles={setFiles}
              instantUpload={false}
              allowFileEncode={true}
              allowMultiple={false}
              required={true}
              credits={false}
              storeAsFile={true}
              labelIdle={editableTranslateString('user_import.drop_or_choose_file', 'Dateien hier ablegen oder zum Hochladen anklicken')}
            />

            {isCsv && (
              <div className="form-group text-center">
                {isAutoDetected && (
                  <div className="mb-2">
                    <div className="alert alert-info d-inline-block mb-0 py-1 px-2">
                      <small>{editableTranslate('user_import.delimiter_auto_detected', 'Automatische Trennzeichenerkennung eingeschaltet. Bei Bedarf manuell wechseln.')}</small>
                    </div>
                  </div>
                )}
                <div>
                  <label className="peb-field__label d-inline-block mr-2" htmlFor="import-delimiter">
                    {editableTranslate('user_import.delimiter', 'Trennzeichen')}
                  </label>
                  <div className="d-inline-block">
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <NativeSelect data-testid="delimiter-select" id="import-delimiter" value={delimiter} onChange={(e) => { setDelimiter(e.target.value); setIsAutoDetected(false); }}>
                          <option value=",">{translate('user_import.delimiter.comma', 'Komma')} (,)</option>
                          <option value=";">{translate('user_import.delimiter.semicolon', 'Semikolon')} (;)</option>
                          <option value="&#9;">{translate('user_import.delimiter.tab', 'Tabulator')}</option>
                          <option value="|">{translate('user_import.delimiter.pipe', 'Senkrechter Strich')} (|)</option>
                          <option value="custom">{translate('user_import.delimiter.custom', 'Benutzerdefiniert')}</option>
                        </NativeSelect>
                      </div>
                      {delimiter === 'custom' && (
                        <Input data-testid="custom-delimiter-input"
                          type="text"
                          className="max-w-100"
                          autoFocus
                          value={customDelimiter}
                          onChange={(e) => setCustomDelimiter(e.target.value)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>

        <div className="text-center mt-1">
          <Button variant="secondary" onClick={() => setMode('view')} data-testid="cancel-button" className="mr-2">
            {editableTranslate('button.cancel', 'Abbrechen')}
          </Button>
          <Button variant="primary" onClick={validateAndSubmit} loading={loading || checkingPeriods} disabled={!files || files.length < 1} data-testid="next-button">{editableTranslate('button.next', 'Weiter')}</Button>
        </div>
      </div>
      <ImportPeriodWarningModal
        show={warningState.show}
        isLoading={loading}
        title={editableTranslate('user_import.period_warning_title', 'Wichtiger Perioden-Hinweis')}
        warningText={editableTranslate('user_import.period_warning_body', 'Dieser Import wird in der aktuell ausgewaehlten Periode erstellt.')}
        currentPeriodLabel={editableTranslate('user_import.period_warning_current_period', 'Aktuelle Periode:')}
        periodName={warningState.activePeriodName}
        detailText={editableTranslate('user_import.period_warning_detail', 'Falls dies nicht die gewuenschte Zielperiode ist, wechseln Sie zuerst die Periode und starten Sie den Import danach erneut.')}
        cancelLabel={editableTranslate('button.cancel', 'Abbrechen')}
        continueLabel={editableTranslate('button.continue', 'Fortfahren')}
        onCancel={handleCancelPeriodWarning}
        onContinue={handleConfirmPeriodWarning}
      />
    </div>
  );
}
